import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, from, map } from "rxjs";
import { environment } from "src/environments/environment";

const SENSINGTEX_URL = environment.sensingtex_url

export interface Patient {
    id: string,
    creationDate?: Date,
    managerId: string,
    isSelected: boolean,
    name: string,
    weight: number,
    height: number
}

export enum AnalysisType {
    Static = "Static",
    Balance = "Balance",
    Dynamic = "Dynamic"
}

export interface Analysis {
    id: string,
    userId: string,
    patientId: string,
    deviceId: string,
    type: string,
    mapPreview: number[][],
    additionalParameters: any,
    creationDate?: string
}

export interface DeviceStatus {
    _id: {
        $oid: string
    };
    deviceFunctional: boolean;
    company: string;
    imageSaved: boolean;
    patientStatus: string;
    deviceId: {
        $oid: string
    };
}

@Injectable({
    providedIn: 'root'
})

export class SensingtexService {

    constructor(
        private http: HttpClient,
        private router: Router) { }

    private async fetchAllPagesFromChiropodist(url: string): Promise<Analysis[]> {
        let page = 1;
        let allAnalyses: Analysis[] = [];
        let hasMoreData = true;

        while (hasMoreData) {
            const response: any = await this.http.get<any>(`${url}/${page}`).toPromise();
            const analyses = response.analyses.map((analysis: any) => ({
                id: analysis._id.$oid,
                creationDate: analysis.creationDate.$date,
                deviceId: analysis.deviceId.$oid,
                type: analysis.analysisType,
                mapPreview: analysis.mapPreview,
                patientId: analysis.patientId,
                additionalParameters: analysis.additionalParameters
            }));
            allAnalyses = allAnalyses.concat(analyses);
            page++;
            hasMoreData = analyses.length > 0;
        }

        return allAnalyses;
    }

    private async fetchAllPagesByPatientId(url: string, patientId: string): Promise<Analysis[]> {
        let page = 1;
        let allAnalyses: Analysis[] = [];
        let hasMoreData = true;

        while (hasMoreData) {
            const response: any = await this.http.get<any>(`${url}/${page}/${patientId}`).toPromise();
            const analyses = response.analyses.map((analysis: any) => ({
                id: analysis._id.$oid,
                creationDate: analysis.creationDate.$date,
                deviceId: analysis.deviceId.$oid,
                type: analysis.analysisType,
                mapPreview: analysis.mapPreview,
                patientId: analysis.patientId,
                additionalParameters: analysis.additionalParameters
            }));
            allAnalyses = allAnalyses.concat(analyses);
            page++;
            hasMoreData = analyses.length > 0;
        }

        return allAnalyses;
    }


    getPatients(managerId: string): Observable<Patient[]> {
        const url = `${SENSINGTEX_URL}/podometry/patients/${managerId}`

        return this.http.get<any[]>(url).pipe(
            map(response => {
                return response.map(patientData => {
                    return {
                        id: patientData._id.$oid,
                        creationDate: patientData.creationDate.$date,
                        managerId: patientData.managerId,
                        isSelected: patientData.isSelected,
                        name: patientData.name,
                        weight: patientData.weight,
                        height: patientData.height
                    };
                });
            })
        );
    }

    getAllAnalysesFromChiropodist(managerId: string, hardwareId: string): Observable<Analysis[]> {
        const url = `${SENSINGTEX_URL}/podometryAnalysisPage/${managerId}/${hardwareId}`

        return from(this.fetchAllPagesFromChiropodist(url))
    }

    getAllAnalysesByPatientId(patientId: string, managerId: string, hardwareId: string): Observable<Analysis[]> {
        const url = `${SENSINGTEX_URL}/podometryAnalysisPatient/${managerId}/${hardwareId}`

        return from(this.fetchAllPagesByPatientId(url, patientId))
    }

    getSelectedPatient(managerId: string): Observable<Patient> {
        const url = `${SENSINGTEX_URL}/podometry/patients/select/${managerId}`

        return this.http.get<Patient>(url)
    }

    selectPatientToDoAnalysis(patient: Patient, managerId: string) {
        const url = `${SENSINGTEX_URL}/podometry/patient/select/${managerId}`

        const body = {
            "selection": patient.id
        }

        return this.http.put(url, body).pipe(map((resp) => resp))
    }

    requestAnalysis(patient: Patient, analysisType, hardwareSerial: string, managerId: string) {
        const url = `${SENSINGTEX_URL}/iotRequests/${hardwareSerial}`

        const body = {
            "requestId": analysisType,
            "requestBody": managerId
        }

        return this.http.post(url, body).pipe(
            map((resp) => resp)
        )
    }

    createPatient(formData, managerId: string): Observable<Patient> {
        const url = `${SENSINGTEX_URL}/podometry/patients/${managerId}`

        return this.http.post<any>(url, formData).pipe(
            map(patientData => {
                return {
                    id: patientData._id.$oid,
                    creationDate: patientData.creationDate.$date,
                    managerId: patientData.managerId,
                    isSelected: patientData.isSelected,
                    name: patientData.name,
                    weight: patientData.weight,
                    height: patientData.height
                }
            })
        )
    }

    updatePatient(patientData: Patient) {
        const url = `${SENSINGTEX_URL}/podometry/patient/${patientData.id}`


        return this.http.put(url, patientData)
    }

    deleteAnalysis(analysisId: string) {
        const url = `${SENSINGTEX_URL}/podometryAnalysis/specific/${analysisId}`

        return this.http.delete(url).pipe(map((resp) => resp))
    }

    getUserDevices(managerId: string) {
        const url = `${SENSINGTEX_URL}/userDevices/${managerId}`

        return this.http.get(url)
    }

    getDeviceStatus(deviceId: string) {
        const url = `${SENSINGTEX_URL}/deviceConfiguration/${deviceId}`

        return this.http.get(url)
    }
}